import { Component, Input, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { FormArray, FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { Yarn } from '../../../models/yarns/yarn';
import { YarnCalculationsService } from '../../../services/yarn-calculations.service';
import { EnumService } from '../../../services/enum.service';
import { EnumValues } from '@card-software/library';
import { YarnProxyService } from '../../../proxies/item/yarn.proxy.service';
import { CompositionProxyService } from '../../../proxies/item/composition-proxy.service';
import { Composition } from '../../../models/composition/composition';

@Component({
  selector: 'app-yarn-info',
  templateUrl: './yarn-info.component.html',
  styleUrls: ['./yarn-info.component.scss'],
})
export class YarnInfoComponent implements OnInit, OnChanges {
  @Input() canEdit = false;
  @Input() yarnInfo!: FormGroup;
  @Input() readonly = false;
  @Input() needles = 0;
  @Input() parentFormSubmitted = false;
  /** Hide the built-in "Yarns" heading when the host already titles the section (e.g. a p-panel header). */
  @Input() showTitle = true;
  // yarnPosition Enum
  yarnPosition: EnumValues;
  yarnPositionOptions: { label: string; value: number }[] = [];
  private allCompositions: Composition[] = [];
  private compositionsLoaded = false;

  yarnStatuses: EnumValues = {};

  constructor(
    private yarnSearchService: YarnProxyService,
    private yarnCalculationsService: YarnCalculationsService,
    private getEnumService: EnumService,
    private compositionProxyService: CompositionProxyService,
    private router: Router,
  ) {
    this.yarnPosition = this.getEnumService.getEnum('YarnPosition', 'itemEnums').values;

    this.yarnPositionOptions = Object.keys(this.yarnPosition).map((key) => {
      const numKey = parseInt(key);
      return {
        label: this.yarnPosition[numKey],
        value: numKey,
      };
    });

    this.yarnStatuses = this.getEnumService.getEnum('ProductDevelopmentStatus', 'itemEnums').values;
  }

  ngOnInit(): void {
    this.loadAllCompositions();

    this.yarnInfo.valueChanges.subscribe(() => {
      if (this.readonly) {
        this.disableFormControls();
        return;
      }
      if (this.needles === 0) {
        this.yarnEntries.controls.forEach((formGroup) => {
          formGroup.patchValue(
            {
              mtsRev: null,
              kgRev: null,
              lbsRev: null,
              loopsPerNeedle: null,
            },
            { emitEvent: false },
          );
        });
      } else {
        this.yarnEntries.controls.forEach((formGroup) => {
          const loopLength = formGroup.get('loopLength')?.value;
          const cones = formGroup.get('cones')?.value;
          const count = formGroup.get('count')?.value;
          if (!loopLength || !cones || !count) {
            formGroup.patchValue(
              {
                mtsRev: null,
                kgRev: null,
                lbsRev: null,
                loopsPerNeedle: null,
              },
              { emitEvent: false },
            );
            return;
          }
          const calculations = this.yarnCalculationsService.calculateRevolutionMetrics(this.needles, loopLength, cones, count);
          formGroup.patchValue(
            {
              mtsRev: calculations.metersPerRev,
              kgRev: calculations.kgPerRev,
              lbsRev: calculations.lbsPerRev,
              loopsPerNeedle: calculations.loopsPerNeedle,
            },
            { emitEvent: false },
          );
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.readonly) return;
    if (changes['needles']) {
      if (changes['needles'].currentValue === 0) {
        this.yarnEntries.controls.forEach((formGroup) => {
          formGroup.patchValue({
            mtsRev: null,
            kgRev: null,
            lbsRev: null,
            loopsPerNeedle: null,
          });
        });
      } else {
        this.yarnEntries.controls.forEach((formGroup) => {
          const loopLength = formGroup.get('loopLength')?.value;
          const cones = formGroup.get('cones')?.value;
          const count = formGroup.get('count')?.value;
          if (!loopLength || !cones || !count) return;
          const calculations = this.yarnCalculationsService.calculateRevolutionMetrics(this.needles, loopLength, cones, count);
          formGroup.patchValue({
            mtsRev: calculations.metersPerRev,
            kgRev: calculations.kgPerRev,
            lbsRev: calculations.lbsPerRev,
            loopsPerNeedle: calculations.loopsPerNeedle,
          });
        });
      }
    }
  }

  get yarnEntries(): FormArray<FormGroup> {
    return this.yarnInfo.get('yarnEntries') as FormArray<FormGroup>;
  }

  get allowToAddNewYarn(): boolean {
    //check that if all yarn entries have a valid selected yarn
    return this.yarnEntries.controls.every((group) => {
      const selectedYarn = group.get('selectedYarn')?.value;
      return selectedYarn !== null && selectedYarn !== undefined;
    });
  }

  createYarnFormGroup(): FormGroup {
    const group = new FormGroup({
      selectedYarn: new FormControl<Yarn | null>(null, Validators.required),
      yarnPosition: new FormControl<number | null>(this.readonly ? { value: null, disabled: true } : null, Validators.required),
      loopLength: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
      loopsPerNeedle: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
      cones: new FormControl<number | null>(this.readonly ? { value: null, disabled: true } : null, [
        Validators.required,
        Validators.min(1),
      ]),
      usePercentage: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
      count: new FormControl<number | null>(this.readonly ? { value: null, disabled: true } : null),
      mtsRev: new FormControl<number | null>(this.readonly ? { value: null, disabled: true } : null),
      kgRev: new FormControl<number | null>(this.readonly ? { value: null, disabled: true } : null),
      lbsRev: new FormControl<number | null>(this.readonly ? { value: null, disabled: true } : null),
    });

    group.get('selectedYarn')?.valueChanges.subscribe((yarn: Yarn | null) => {
      if (this.readonly) return;
      if (yarn) {
        group.patchValue({
          count: yarn.yarnCount ? yarn.yarnCount : 0,
          yarnPosition: null,
          loopLength: null,
          loopsPerNeedle: null,
          cones: null,
          usePercentage: null,
          mtsRev: null,
          kgRev: null,
          lbsRev: null,
        });
      }
    });

    return group;
  }

  private disableFormControls(): void {
    this.yarnEntries.controls.forEach((group) => {
      const disabledFields = [
        'selectedYarn',
        'yarnPosition',
        'loopLength',
        'loopsPerNeedle',
        'cones',
        'usePercentage',
        'count',
        'mtsRev',
        'kgRev',
        'lbsRev',
      ];

      // Disable fields
      disabledFields.forEach((fieldName) => {
        const control = group.get(fieldName);
        if (control && control.enabled) {
          control.disable();
        }
      });
    });
  }

  addYarn(): void {
    this.yarnEntries.push(this.createYarnFormGroup());
  }

  removeYarn(index: number): void {
    this.yarnEntries.removeAt(index);
  }

  /**
   * One autocomplete per row, so the fetch is curried by row index: each row's directive gets its own
   * function and its own results list. Only a typed search clears the row; opening the dropdown just lists yarns.
   */
  public fetchYarns =
    (index: number) =>
    (query: string): Observable<Yarn[]> => {
      if (query) this.resetYarn(index);

      return this.yarnSearchService
        .searchYarns(
          {
            longDescription: query,
            code: query,
          },
          true,
        )
        .pipe(
          map((response) => (response.data?.items ?? []) as Yarn[]),
          catchError(() => of([] as Yarn[])),
        );
    };

  private loadAllCompositions(): void {
    if (this.compositionsLoaded) {
      return;
    }

    this.compositionProxyService.getAllCompositions().subscribe({
      next: (response) => {
        this.allCompositions = response.data as Composition[];
        this.compositionsLoaded = true;
      },
      error: (error) => {
        console.error('Error loading compositions:', error);
        this.compositionsLoaded = false;
      },
    });
  }

  getCompositionColor(compositionCode: string): string {
    switch (compositionCode.toLowerCase()) {
      case 'cotton':
        return '#001a70';
      case 'poly':
        return '#e65c4f';
      case 'recycledblackpoly':
        return '#5fa535';
      case 'rayon':
        return '#009999';
      case 'blackpoly':
        return '#5AA29D';
      case 'polyrecycle':
        return '#5AA29D';
      default:
        return '#777';
    }
  }

  getCompositionLabel(compositionCode: string): string {
    if (!this.compositionsLoaded || this.allCompositions.length === 0) {
      return compositionCode;
    }

    const composition = this.allCompositions.find((comp) => comp.code === compositionCode);

    if (composition?.displayName) {
      if (typeof composition.displayName === 'string') {
        return composition.displayName;
      } else if (composition.displayName && typeof composition.displayName === 'object') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const translationObj = composition.displayName as any;
        return translationObj.en || translationObj.es || compositionCode;
      }
    }
    return compositionCode;
  }

  private checkForDuplicateYarn(selectedYarn: Yarn, currentGroup: FormGroup): boolean {
    const yarnDescription = selectedYarn.longDescription;

    const yarnReference = yarnDescription;

    return this.yarnEntries.controls.some((group) => {
      if (group === currentGroup) return false;

      const existingYarn = group.get('selectedYarn')?.value;
      if (!existingYarn) return false;

      const existingYarnReference = existingYarn.longDescription;

      return existingYarnReference === yarnReference;
    });
  }
  resetYarn(index: number): void {
    const group = this.yarnEntries.at(index) as FormGroup;
    group.patchValue({
      count: 0,
      yarnPosition: null,
      loopLength: null,
      loopsPerNeedle: null,
      cones: null,
      usePercentage: null,
      mtsRev: null,
      kgRev: null,
      lbsRev: null,
    });
  }

  getStatusLabel(status: number): string {
    return this.yarnStatuses[status] ?? '';
  }

  getStatusSeverity(status: number): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case 4:
        return 'success'; // ReadyForProduction
      case 3:
        return 'info'; // Approved
      case 2:
        return 'warn'; // UnderReview
      case 1:
        return 'secondary'; // InDevelopment
      case 0:
        return 'contrast'; // Draft
      case 98:
        return 'danger'; // Inactive
      case 99:
        return 'danger'; // Rejected
      default:
        return 'secondary';
    }
  }

  navigateToYarn(yarn: Yarn): void {
    if (!yarn?.code) return;
    const urlTree = this.router.createUrlTree(['/yarns'], {
      queryParams: { code: yarn.code },
    });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }
}
